import { Box, IconButton, Tooltip } from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';

import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import type { ArtworkImage } from '../../types/artwork';

type SortableThumbnailProps = {
  image: ArtworkImage;
  index: number;
  totalImages: number;
  moveImage: (currentIndex: number, newIndex: number) => void;
  selected: boolean;
  onSelect: (imageUrl: string) => void;
};

export default function SortableThumbnail({
  image,
  index,
  totalImages,
  moveImage,
  selected,
  onSelect,
}: SortableThumbnailProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: image.url,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        width: 120,
        opacity: isDragging ? 0.6 : 1,
      }}
    >
      <Box
        component="img"
        {...attributes}
        {...listeners}
        src={image.url}
        alt={`Artwork image ${index + 1}`}
        onPointerDownCapture={() => onSelect(image.url)}
        sx={{
          display: 'block',
          width: 120,
          height: 120,
          objectFit: 'cover',
          border: selected ? 3 : 1,
          borderColor: selected ? 'primary.main' : 'divider',
          borderRadius: 1,
          cursor: isDragging ? 'grabbing' : 'grab',
          boxSizing: 'border-box',
        }}
      />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          gap: 0.5,
          mt: 0.5,
        }}
      >
        <Tooltip title="Move image left">
          <span>
            <IconButton
              type="button"
              size="small"
              disabled={index === 0}
              onClick={() => moveImage(index, index - 1)}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Move image right">
          <span>
            <IconButton
              type="button"
              size="small"
              disabled={index === totalImages - 1}
              onClick={() => moveImage(index, index + 1)}
            >
              <ArrowForwardIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}